<template>

  <nav class="navbar" tabindex="-1">

    <div class="nav-inner justify-between">

      <!-- The DESKTOP part -->
      <div class="justify-between flex-auto font-light flex items-center">

        <!-- Search Button at the start -->
        <div class="flex items-center w-full pr-2">
          <button 
            v-if="!!supabaseUser && company" 
            class="btn btn-ghost bg-base-200 mr-2 w-full flex justify-start font-light" 
            @click="openCommandPalette"
            :title="`Search (${keyboardShortcut})`"
          >
            <span class="text-base-content/60">Search...  {{ keyboardShortcut }}</span>
            <!-- <kbd class="kbd kbd-sm hidden sm:inline-flex"></kbd> -->
          </button>
        </div>

        <!-- Other items at the end -->
        <div class="flex items-center">

          <!-- if there's a company selected, show a dropdown on an icon like we do for the user -->
          <div v-if="company" class="flex items-center">

            <!-- Feedback Button -->
            <button v-if="!!supabaseUser" class="btn btn-outline btn-sm mr-2" @click="shareFeedback"
              title="Send Feedback">
              Feedback
            </button>

            <DevOnly>
              <Transition name="fade">

                <div class="dropdown dropdown-hover dropdown-end mr-2">
                  <label tabindex="0" class="btn btn-ghost btn-circle">
                    <img src="@/assets/icons/warning-2.svg" width="25" alt="Company" class="icon">
                  </label>
                  <ul tabindex="0" class="p-4 dropdown-content z-50 bg-base-200 shadow rounded-box w-64">
                    <li>
                      <span class="muted text-xs">Realtime Channels</span>
                    </li>
                    <li v-for="(status, channel) in subscribedChannels.channels">
                      <span class="text-sm font-semibold">
                        {{ channel }} - {{ status }}
                      </span>
                    </li>
                  </ul>
                </div>
              </Transition>
            </DevOnly>


            <div class="dropdown dropdown-hover dropdown-end mr-2">
              <label tabindex="0" class="btn btn-ghost btn-circle">
                <img src="@/assets/icons/building-4.svg" width="25" alt="Company" class="icon">
              </label>
              <ul tabindex="0" class="p-2 dropdown-content z-50 menu bg-base-200 shadow rounded-box w-52">
                <li>
                  <NuxtLink :to="`/company/${company.public_id}`" class="justify-between w-full">
                    Home
                  </NuxtLink>
                </li>
                <li>
                  <NuxtLink :to="`/company/${company.public_id}/foundation`" class="justify-between w-full">
                    Company Foundation
                  </NuxtLink>
                </li>
                <li>
                  <NuxtLink :to="`/company/${company.public_id}/insights`" class="justify-between w-full">
                    Insights
                  </NuxtLink>
                </li>
                <li>
                  <NuxtLink :to="`/company/${company.public_id}/features`" class="justify-between w-full">
                    Features
                  </NuxtLink>
                </li>
                <li>
                  <NuxtLink :to="`/company/${company.public_id}/projects`" class="justify-between w-full">
                    Projects
                  </NuxtLink>
                </li>
                <li>
                  <NuxtLink :to="`/company/${company.public_id}/integrations`" class="justify-between w-full">
                    Integrations
                  </NuxtLink>
                </li>
                <li class="mt-10">
                  <NuxtLink :to="`/company/${company.public_id}/settings`" class="justify-between w-full text-error">
                    Settings
                  </NuxtLink>
                </li>
              </ul>
            </div>
          </div>


          <div v-if="!!supabaseUser" class="dropdown dropdown-hover dropdown-end">
            <label tabindex="0" class="btn btn-ghost btn-circle">
              <img src="@/assets/icons/profile.svg" width="25" alt="Acount" class="icon">
            </label>


            <ul tabindex="0" class="p-2 dropdown-content z-50 menu bg-base-200 shadow rounded-box w-52">
              <li>
                <NuxtLink :to="`/profile/${supabaseUser.id}`" class="justify-between w-full">
                  Profile
                </NuxtLink>
              </li>


              <li class="mt-10"><a @click="signOut" class="w-full">Logout</a></li>
            </ul>
          </div>


        </div>

      </div>

    </div>

  </nav>

  <!-- Command Palette Modal -->
  <CommandPaletteModal ref="commandPaletteModalRef" />

</template>

<style scoped>

[data-theme='dark'] #themeIcon #sunLogo {
  display: inline;
}

[data-theme='light'] #themeIcon #moonLogo {
  display: inline;
}

.navbar .navItem {
  font-weight:400;
  @apply text-base p-3;
}

.dropdown .btn:hover,
.dropdown .btn:active,
.dropdown .btn:focus {
  /* background: none; */
  --tw-bg-opacity: 0.1;
}

[data-theme='dark'] nav.scrolled {
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

nav {
  @apply bg-base-100;
  position: relative;
  top:0;
  transition: box-shadow 0.1s ease-in-out;
}

nav.scrolled {
  @apply shadow-2xl;
}

.nav-inner  {
  margin: auto;
  width: 100%;
  max-width: 1600px;
}
    
</style>

<script setup>
import { useEventListener } from '@vueuse/core'

const colorScheme = useColorScheme()
const supabaseUser = useSupabaseUser()
const supabaseUserData = useSupabaseUserData()
const company = useCompanyData()
const subscribedChannels = useSubscribedChannels()
const { sendMessage } = useAiChat()

const commandPaletteModalRef = ref(null)

// Detect OS for keyboard shortcut display
const isMac = ref(false)
const keyboardShortcut = computed(() => isMac.value ? '⌘K' : 'Ctrl+K')

// Keyboard shortcuts - handle CMD+K / CTRL+K
useEventListener('keydown', (event) => {
  // Check if CMD+K (Mac) or CTRL+K (Windows/Linux) is pressed
  const isCmdK = (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k'
  
  if (isCmdK && company.value) {
    // Prevent default browser behavior (e.g., Firefox search box)
    event.preventDefault()
    event.stopPropagation()
    openCommandPalette()
  }
})

const openCommandPalette = () => {
  commandPaletteModalRef.value?.openModal()
}

onMounted(() => {
  console.log("Topnav mounted")
  
  // Detect if user is on Mac
  isMac.value = /Mac|iPhone|iPod|iPad/i.test(navigator.platform) || 
                /Mac|iPhone|iPod|iPad/i.test(navigator.userAgent)

  if(supabaseUserData.value) {
    console.log("supabaseUserData:", supabaseUserData.value)
    colorScheme.value = supabaseUserData.value.personal_settings.theme || 'myLight'
  }

})


watch(supabaseUserData, (newUserData) => {
  console.log("supabaseUserData changed:", newUserData)
  colorScheme.value = newUserData?.personal_settings?.theme || 'myLight'
})

const shareFeedback = () => {
  sendMessage("I'd like to share feedback or suggestions about SupaPM", true)
}

</script>